import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * Shipping protection in the cart.
 *
 * The add-on is a normal product that is presented as a service: a switch adds or removes
 * it, its cart line is hidden and it is left out of the article count (see
 * snippets/vm-cart-protection.liquid). After every change the theme's own cart-update event
 * is dispatched, so the drawer, the cart page and the cart icon re-render themselves.
 */

const SOURCE = 'vm-shipping-protection';
const DEFAULT_HANDLE = 'protection-dexpedition';
const JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json' };

const cartRoot = () => window.Shopify?.routes?.root ?? '/';

const protectionHandle = () =>
  document.querySelector('vm-shipping-protection')?.dataset.handle || DEFAULT_HANDLE;

/** Number of articles in the cart, leaving the protection add-on out. */
function articleCount(cart) {
  const handle = protectionHandle();
  return cart.items.reduce((count, item) => (item.handle === handle ? count : count + item.quantity), 0);
}

async function cartRequest(path, body) {
  const response = await fetch(`${cartRoot()}${path}`, {
    method: body ? 'POST' : 'GET',
    headers: JSON_HEADERS,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!response.ok) throw new Error(`Cart request ${path} failed with status ${response.status}`);
  return response.json();
}

/** Lets the theme's cart components re-render for the given cart. */
function announce(cart) {
  document.dispatchEvent(new CartUpdateEvent(cart, SOURCE, { itemCount: articleCount(cart), source: SOURCE }));
}

/** Writes the article count into every cart bubble, and into the theme's page-restore cache. */
function showArticleCount(count) {
  for (const bubble of document.querySelectorAll('[ref="cartBubbleCount"]')) {
    bubble.textContent = count < 100 ? String(count) : '';
  }
  try {
    sessionStorage.setItem('cart-count', JSON.stringify({ value: String(count), timestamp: Date.now() }));
  } catch (_) {
    // Storage can be unavailable; the bubbles are already correct.
  }
}

let removingOrphan = false;

/** Protection without anything to protect is taken out of the cart again. */
async function removeOrphanedProtection() {
  if (removingOrphan) return;
  removingOrphan = true;

  try {
    const cart = await cartRequest('cart.js');
    const handle = protectionHandle();
    const orphans = cart.items.filter((item) => item.handle === handle);
    if (orphans.length === 0 || orphans.length !== cart.items.length) return;

    const updates = Object.fromEntries(orphans.map((item) => [item.key, 0]));
    announce(await cartRequest('cart/update.js', { updates }));
  } catch (error) {
    console.error(error);
  } finally {
    removingOrphan = false;
  }
}

// The theme counts every cart line. Whenever a cart update carries the full cart, the
// count shown to the customer is corrected to articles only.
document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  const cart = event.detail?.resource;
  if (!cart || !Array.isArray(cart.items) || typeof cart.item_count !== 'number') return;

  const count = articleCount(cart);
  if (count === cart.item_count) return;

  if (count === 0) {
    removeOrphanedProtection();
  } else if (event.detail?.data?.source !== SOURCE) {
    // Runs after the theme's own listeners have written their count.
    queueMicrotask(() => showArticleCount(count));
  }
});

class VmShippingProtection extends HTMLElement {
  static observedAttributes = ['data-orphan'];

  connectedCallback() {
    this.addEventListener('change', this.#onChange);
    if (this.hasAttribute('data-orphan')) removeOrphanedProtection();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
  }

  attributeChangedCallback(name, _oldValue, value) {
    if (name === 'data-orphan' && value !== null && this.isConnected) removeOrphanedProtection();
  }

  #onChange = async (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    const variantId = Number(this.dataset.variantId);
    if (!variantId) return;

    const wanted = input.checked;
    input.disabled = true;

    try {
      if (wanted) {
        await cartRequest('cart/add.js', { items: [{ id: variantId, quantity: 1 }] });
      } else {
        await cartRequest('cart/update.js', { updates: { [variantId]: 0 } });
      }
      announce(await cartRequest('cart.js'));
    } catch (error) {
      console.error(error);
      input.checked = !wanted;
    } finally {
      input.disabled = false;
    }
  };
}

if (!customElements.get('vm-shipping-protection')) {
  customElements.define('vm-shipping-protection', VmShippingProtection);
}
